// A change over's studio (full screen): a live 3D preview that follows every
// edit instantly, the final video, and Design / AI changes / Details tabs.
// Saving (or an AI change) makes a new version and draws the final video.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FiX, FiSave, FiRotateCcw, FiDownload, FiCopy, FiTrash2, FiPlay, FiPause, FiRefreshCw, FiChevronDown, FiPlus, FiZap, FiFilm, FiImage, FiEye,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { changeoverService } from '../services/apiService';
import { useT } from '../utils/i18n';
import { showError } from './FullStory/shared';
import { StatusLine, FavoriteButton, TagInput, isBusy } from './Changeovers';

const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);
const clone = (x) => JSON.parse(JSON.stringify(x));

// ─── small controls ────────────────────────────────────────────────────
const Section = ({ title, children, open: initial = false }) => {
  const [open, setOpen] = useState(initial);
  return (
    <div className="border border-gray-200 rounded-xl bg-white">
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between px-3.5 py-2.5 text-sm font-semibold text-gray-800">
        {title}
        <FiChevronDown size={15} className={`text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="px-3.5 pb-3.5 space-y-3">{children}</div>}
    </div>
  );
};
const Field = ({ label, children, hint }) => (
  <div>
    <p className="text-xs font-medium text-gray-600 mb-1">{label}{hint && <span className="font-normal text-gray-400"> · {hint}</span>}</p>
    {children}
  </div>
);
const Choice = ({ value, options, onChange }) => {
  const t = useT();
  return (
    <div className="flex flex-wrap gap-1">
      {options.map((o) => (
        <button key={o} type="button" onClick={() => onChange(o)} className={`text-xs rounded-lg px-2.5 py-1 border transition-colors ${value === o ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'}`}>
          {t(`changeovers.opt.${o}`)}
        </button>
      ))}
    </div>
  );
};
const Slider = ({ value, min, max, step = 0.01, onChange, format = (v) => v }) => (
  <div className="flex items-center gap-2">
    <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} className="flex-1 accent-primary-600" />
    <span className="text-xs text-gray-500 w-12 text-right tabular-nums">{format(value)}</span>
  </div>
);
const Color = ({ value, onChange, allowNone = false }) => {
  const none = value === 'none';
  return (
    <span className="inline-flex items-center gap-1">
      <input type="color" value={none ? '#ffffff' : value} onChange={(e) => onChange(e.target.value)} className={`w-7 h-7 rounded border border-gray-200 cursor-pointer p-0 ${none ? 'opacity-30' : ''}`} />
      <input value={value} onChange={(e) => { const v = e.target.value.trim(); if (/^#[0-9a-f]{6}$/i.test(v) || v === 'none') onChange(v.toLowerCase()); }} className="w-[74px] text-[11px] font-mono border border-gray-200 rounded px-1.5 py-1" />
      {allowNone && <button type="button" onClick={() => onChange(none ? '#e8382b' : 'none')} className="text-[10px] text-gray-500 underline">{none ? '+' : '×'}</button>}
    </span>
  );
};
const ColorList = ({ values, onChange, min = 1, max = 6 }) => (
  <div className="flex flex-wrap items-center gap-2">
    {values.map((c, i) => (
      // eslint-disable-next-line react/no-array-index-key
      <span key={i} className="inline-flex items-center gap-0.5">
        <Color value={c} onChange={(v) => onChange(values.map((x, j) => (j === i ? v : x)))} />
        {values.length > min && <button type="button" onClick={() => onChange(values.filter((_, j) => j !== i))} className="text-gray-300 hover:text-red-500"><FiX size={12} /></button>}
      </span>
    ))}
    {values.length < max && <button type="button" onClick={() => onChange([...values, values[values.length - 1] || '#ffffff'])} className="w-7 h-7 rounded border border-dashed border-gray-300 text-gray-400 flex items-center justify-center"><FiPlus size={12} /></button>}
  </div>
);
const Toggle = ({ checked, onChange, label }) => (
  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="rounded accent-primary-600" />
    {label}
  </label>
);
const FontSelect = ({ value, fonts, onChange }) => {
  const t = useT();
  const groups = [['english', t('changeovers.fontGroup.english')], ['display', t('changeovers.fontGroup.display')], ['sans', t('changeovers.fontGroup.sans')], ['serif', t('changeovers.fontGroup.serif')], ['handwriting', t('changeovers.fontGroup.handwriting')]];
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className="input text-xs py-1.5 w-full">
      {groups.map(([g, label]) => {
        const list = fonts.filter((f) => (f.category || 'display') === g);
        return list.length ? <optgroup key={g} label={label}>{list.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}</optgroup> : null;
      })}
    </select>
  );
};

// ─── live preview ──────────────────────────────────────────────────────
const LivePreview = ({ spec }) => {
  const t = useT();
  const frame = useRef(null);
  const [ready, setReady] = useState(false);
  const [built, setBuilt] = useState(false);
  const [error, setError] = useState('');
  const [playing, setPlaying] = useState(true);
  const [speed, setSpeed] = useState(1);
  const [time, setTime] = useState({ t: 0, total: 1 });
  const lastTime = useRef(0);
  const post = (m) => frame.current?.contentWindow?.postMessage(m, '*');

  useEffect(() => {
    const onMessage = (e) => {
      if (e.source !== frame.current?.contentWindow) return;
      const m = e.data || {};
      if (m.type === 'changeover:ready') setReady(true);
      else if (m.type === 'changeover:built') { setBuilt(true); setError(''); }
      else if (m.type === 'changeover:error') setError(m.message);
      else if (m.type === 'changeover:time' && performance.now() - lastTime.current > 100) { lastTime.current = performance.now(); setTime({ t: m.t, total: m.total }); }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // Every edit reaches the stage (debounced): rebuilt in a fraction of a second.
  useEffect(() => {
    if (!ready || !spec) return undefined;
    const timer = setTimeout(() => post({ type: 'changeover:spec', spec, footage: ['footage/0.mp4', 'footage/1.mp4'] }), 120);
    return () => clearTimeout(timer);
  }, [ready, spec]);

  // The page says it is ready when asked (it may have loaded before we listened).
  useEffect(() => {
    if (ready) return undefined;
    const timer = setInterval(() => post({ type: 'changeover:hello' }), 300);
    return () => clearInterval(timer);
  }, [ready]);
  useEffect(() => { post({ type: 'changeover:play', playing }); }, [playing]);
  useEffect(() => { post({ type: 'changeover:speed', speed }); }, [speed]);

  return (
    <div className="space-y-2">
      <div className="relative w-full aspect-video bg-black rounded-xl overflow-hidden">
        <iframe ref={frame} title="live" src={changeoverService.liveUrl()} className="absolute inset-0 w-full h-full border-0" allow="autoplay" />
        {!built && !error && <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-sm gap-2"><FiRefreshCw className="animate-spin" /> {t('changeovers.studio.loadingLive')}</div>}
        {error && <div className="absolute inset-x-0 bottom-0 bg-red-600/90 text-white text-xs p-2">{error}</div>}
      </div>
      <div className="flex items-center gap-2">
        <button type="button" onClick={() => setPlaying((v) => !v)} className="p-2 rounded-lg bg-gray-800 text-white hover:bg-gray-700">{playing ? <FiPause size={14} /> : <FiPlay size={14} />}</button>
        <button type="button" onClick={() => post({ type: 'changeover:seek', t: 0 })} title={t('changeovers.studio.restart')} className="p-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200"><FiRotateCcw size={14} /></button>
        <input
          type="range" min={0} max={time.total} step={0.01} value={Math.min(time.t, time.total)}
          onChange={(e) => { setPlaying(false); post({ type: 'changeover:seek', t: Number(e.target.value) }); setTime((x) => ({ ...x, t: Number(e.target.value) })); }}
          className="flex-1 accent-primary-600"
        />
        <span className="text-xs text-gray-500 tabular-nums w-20 text-right">{time.t.toFixed(2)} / {time.total.toFixed(2)} s</span>
        <select value={speed} onChange={(e) => setSpeed(Number(e.target.value))} className="text-xs border border-gray-200 rounded-lg px-1.5 py-1.5">
          {[0.25, 0.5, 1].map((s) => <option key={s} value={s}>{s}×</option>)}
        </select>
      </div>
    </div>
  );
};

// ─── tabs ──────────────────────────────────────────────────────────────
const DesignTab = ({ draft, up, options }) => {
  const t = useT();
  const O = options.options;
  const L = draft.logo;
  const line = (i, field, v) => up((d) => { d.logo.lines[i][field] = v; });
  const fontFor = (text) => (/[ఀ-౿]/.test(text) ? 'anektelugu-800' : 'anton');
  return (
    <div className="space-y-2.5">
      <Section title={t('changeovers.design.text')} open>
        <Field label={t('changeovers.design.layout')}><Choice value={L.layout} options={O.layout} onChange={(v) => up((d) => { d.logo.layout = v; })} /></Field>
        {L.lines.map((l, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={i} className="rounded-lg bg-gray-50 p-2.5 space-y-2">
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] font-semibold text-gray-500 w-12">{L.layout === 'number' ? t(i === 0 ? 'changeovers.design.label' : 'changeovers.design.strap') : t('changeovers.design.line', { n: i + 1 })}</span>
              <input value={l.text} onChange={(e) => line(i, 'text', e.target.value)} className="input text-sm py-1.5 flex-1" maxLength={40} />
              {L.lines.length > 1 && <button type="button" onClick={() => up((d) => { d.logo.lines.splice(i, 1); })} className="p-1 text-gray-400 hover:text-red-600"><FiTrash2 size={13} /></button>}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <FontSelect value={l.font} fonts={options.fonts} onChange={(v) => line(i, 'font', v)} />
              <Slider value={l.size} min={0.4} max={1.8} onChange={(v) => line(i, 'size', v)} format={(v) => `${Math.round(v * 100)}%`} />
            </div>
            <div className="flex flex-wrap gap-x-3 gap-y-1.5 text-[11px] text-gray-500">
              <span className="flex items-center gap-1">{t('changeovers.design.face')} <Color value={l.face} onChange={(v) => line(i, 'face', v)} /></span>
              <span className="flex items-center gap-1">{t('changeovers.design.side')} <Color value={l.side} onChange={(v) => line(i, 'side', v)} /></span>
              {L.layout !== 'title' && <span className="flex items-center gap-1">{t('changeovers.design.panel')} <Color value={l.panel} allowNone onChange={(v) => line(i, 'panel', v)} /></span>}
            </div>
          </div>
        ))}
        {L.lines.length < 3 && (
          <button type="button" onClick={() => up((d) => { d.logo.lines.push({ ...clone(d.logo.lines[d.logo.lines.length - 1]), text: 'తెలుగు', font: fontFor('తెలుగు'), size: 0.8 }); })} className="btn btn-secondary text-xs flex items-center gap-1.5 py-1.5">
            <FiPlus size={13} /> {t('changeovers.design.addLine')}
          </button>
        )}
        {L.layout === 'number' && (
          <div className="rounded-lg bg-gray-50 p-2.5 space-y-2">
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] font-semibold text-gray-500 w-12">{t('changeovers.design.number')}</span>
              <input value={L.number.text} onChange={(e) => up((d) => { d.logo.number.text = e.target.value; })} className="input text-sm py-1.5 flex-1" maxLength={8} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <FontSelect value={L.number.font} fonts={options.fonts} onChange={(v) => up((d) => { d.logo.number.font = v; })} />
              <Slider value={L.number.size} min={0.4} max={1.8} onChange={(v) => up((d) => { d.logo.number.size = v; })} format={(v) => `${Math.round(v * 100)}%`} />
            </div>
            <div className="flex flex-wrap gap-x-3 text-[11px] text-gray-500">
              <span className="flex items-center gap-1">{t('changeovers.design.face')} <Color value={L.number.face} onChange={(v) => up((d) => { d.logo.number.face = v; })} /></span>
              <span className="flex items-center gap-1">{t('changeovers.design.side')} <Color value={L.number.side} onChange={(v) => up((d) => { d.logo.number.side = v; })} /></span>
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('changeovers.design.textFinish')}><Choice value={L.textFinish} options={O.textFinish} onChange={(v) => up((d) => { d.logo.textFinish = v; })} /></Field>
          <Field label={t('changeovers.design.panelFinish')}><Choice value={L.finish} options={O.finish} onChange={(v) => up((d) => { d.logo.finish = v; })} /></Field>
        </div>
        <div className="grid grid-cols-2 gap-3 items-end">
          <Field label={t('changeovers.design.rim')}><Color value={L.rim} onChange={(v) => up((d) => { d.logo.rim = v; })} /></Field>
          <Toggle checked={L.underline} onChange={(v) => up((d) => { d.logo.underline = v; })} label={t('changeovers.design.underline')} />
        </div>
        <Field label={t('changeovers.design.scale')}><Slider value={L.scale} min={0.5} max={1.4} onChange={(v) => up((d) => { d.logo.scale = v; })} format={(v) => `${Math.round(v * 100)}%`} /></Field>
      </Section>

      <Section title={t('changeovers.design.animation')} open>
        <Field label={t('changeovers.design.duration')}><Slider value={draft.duration} min={1.2} max={5} step={0.05} onChange={(v) => up((d) => { d.duration = v; })} format={(v) => `${v.toFixed(2)} s`} /></Field>
        <Field label={t('changeovers.design.letters')}><Choice value={L.letters} options={O.letters} onChange={(v) => up((d) => { d.logo.letters = v; })} /></Field>
        <Field label={t('changeovers.design.stagger')} hint={t('changeovers.design.staggerHint')}><Slider value={L.stagger} min={0} max={1} onChange={(v) => up((d) => { d.logo.stagger = v; })} format={(v) => `${Math.round(v * 100)}%`} /></Field>
        <Field label={t('changeovers.design.exit')}><Choice value={L.exit} options={O.exit} onChange={(v) => up((d) => { d.logo.exit = v; })} /></Field>
      </Section>

      <Section title={t('changeovers.design.transition')}>
        <Field label={t('changeovers.design.enter')}><Choice value={draft.enter} options={O.enter} onChange={(v) => up((d) => { d.enter = v; })} /></Field>
        <Field label={t('changeovers.design.cover')}><Choice value={draft.cover.style} options={O.coverStyle} onChange={(v) => up((d) => { d.cover.style = v; })} /></Field>
        <Field label={t('changeovers.design.coverColors')}><ColorList values={draft.cover.colors} min={2} max={6} onChange={(v) => up((d) => { d.cover.colors = v; })} /></Field>
        <Field label={t('changeovers.design.accents')}><ColorList values={draft.cover.accents} min={1} max={3} onChange={(v) => up((d) => { d.cover.accents = v; })} /></Field>
        {draft.cover.style === 'bars' && <Field label={t('changeovers.design.angle')}><Slider value={draft.cover.angle} min={-30} max={30} step={1} onChange={(v) => up((d) => { d.cover.angle = v; })} format={(v) => `${v}°`} /></Field>}
        {['bars', 'burst'].includes(draft.cover.style) && <Field label={t('changeovers.design.count')}><Slider value={draft.cover.count} min={draft.cover.style === 'burst' ? 10 : 5} max={draft.cover.style === 'burst' ? 40 : 21} step={1} onChange={(v) => up((d) => { d.cover.count = v; })} /></Field>}
        {draft.cover.style === 'bars' && <Field label={t('changeovers.design.from')}><Choice value={draft.cover.from} options={O.coverFrom} onChange={(v) => up((d) => { d.cover.from = v; })} /></Field>}
      </Section>

      <Section title={t('changeovers.design.effects')}>
        <Field label={t('changeovers.design.emblem')}>
          <div className="flex items-center gap-2 flex-wrap">
            <Choice value={draft.emblem.kind} options={O.emblem} onChange={(v) => up((d) => { d.emblem.kind = v; })} />
            {draft.emblem.kind !== 'none' && <Color value={draft.emblem.color} onChange={(v) => up((d) => { d.emblem.color = v; })} />}
          </div>
        </Field>
        <Field label={t('changeovers.design.particles')} hint={t('changeovers.design.upTo2')}>
          <div className="flex flex-wrap gap-3">
            {O.particles.map((p) => (
              <Toggle key={p} label={t(`changeovers.opt.${p}`)} checked={draft.particles.includes(p)} onChange={(on) => up((d) => { d.particles = on ? [...d.particles.filter((x) => x !== p), p].slice(-2) : d.particles.filter((x) => x !== p); })} />
            ))}
          </div>
        </Field>
        <Field label={t('changeovers.design.lights')}>
          <div className="grid grid-cols-2 gap-1.5">
            {['glint', 'streaks', 'flares', 'spot'].map((k) => <Toggle key={k} label={t(`changeovers.light.${k}`)} checked={draft.lights[k]} onChange={(v) => up((d) => { d.lights[k] = v; })} />)}
          </div>
        </Field>
        <Toggle checked={draft.sound} onChange={(v) => up((d) => { d.sound = v; })} label={t('changeovers.design.sound')} />
      </Section>

      <Section title={t('changeovers.design.camera')}>
        <Field label={t('changeovers.design.cameraEnter')}><Choice value={draft.camera.enter} options={O.cameraEnter} onChange={(v) => up((d) => { d.camera.enter = v; })} /></Field>
        <Field label={t('changeovers.design.cameraHold')}><Choice value={draft.camera.hold} options={O.cameraHold} onChange={(v) => up((d) => { d.camera.hold = v; })} /></Field>
        <Field label={t('changeovers.design.zoom')}><Slider value={draft.camera.zoom} min={0} max={1} onChange={(v) => up((d) => { d.camera.zoom = v; })} format={(v) => `${Math.round(v * 100)}%`} /></Field>
        <Field label={t('changeovers.design.shake')}><Slider value={draft.camera.shake} min={0} max={1} onChange={(v) => up((d) => { d.camera.shake = v; })} format={(v) => `${Math.round(v * 100)}%`} /></Field>
      </Section>

      <Section title={t('changeovers.design.palette')}>
        <p className="text-[11px] text-gray-400">{t('changeovers.design.paletteHint')}</p>
        <div className="grid grid-cols-2 gap-2">
          {['main', 'second', 'accent', 'metal', 'dark'].map((k) => (
            <span key={k} className="flex items-center justify-between gap-1 text-[11px] text-gray-500">{t(`changeovers.palette.${k}`)} <Color value={draft.colors[k]} onChange={(v) => up((d) => { d.colors[k] = v; })} /></span>
          ))}
        </div>
      </Section>
    </div>
  );
};

const QUICK = ['slower', 'faster', 'telugu', 'zoomIn', 'zoomOut', 'bigger', 'premium', 'channelColours', 'sparks', 'curtain', 'shatter', 'calmer'];

const AiTab = ({ rec, draft, onApply, onTry, onRestore, busy }) => {
  const t = useT();
  const [prompt, setPrompt] = useState('');
  const [sending, setSending] = useState(false);
  const apply = async () => {
    setSending(true);
    try { await onApply(prompt.trim()); setPrompt(''); } finally { setSending(false); }
  };
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <p className="text-sm font-medium text-gray-800">{t('changeovers.ai.title')}</p>
        <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={4} maxLength={2000} placeholder={t('changeovers.changePlaceholder')} className="input w-full text-sm" />
        <div className="flex flex-wrap gap-1.5">
          {QUICK.map((q) => (
            <button key={q} type="button" onClick={() => setPrompt((p) => `${p}${p.trim() ? '. ' : ''}${t(`changeovers.quick.${q}`)}`)} className="text-[11px] rounded-full px-2.5 py-1 bg-gray-100 text-gray-700 hover:bg-primary-50 hover:text-primary-700">
              + {t(`changeovers.quick.${q}`)}
            </button>
          ))}
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[11px] text-gray-400">{t('changeovers.ai.hint')}</span>
          <button type="button" onClick={apply} disabled={sending || busy || !prompt.trim()} className="btn btn-primary text-sm flex items-center gap-1.5"><FiZap size={14} /> {sending ? t('changeovers.sending') : t('changeovers.ai.apply')}</button>
        </div>
      </div>
      <div>
        <p className="text-sm font-medium text-gray-800 mb-2">{t('changeovers.ai.versions')}</p>
        <div className="space-y-1.5">
          {[...(rec.versions || [])].reverse().map((v) => {
            const current = v.n === rec.current;
            const trying = same(v.spec, draft) && !current;
            return (
              <div key={v.n} className={`rounded-lg border p-2.5 ${current ? 'border-primary-300 bg-primary-50/40' : 'border-gray-200'}`}>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-gray-800">v{v.n}</span>
                  <span className="text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 bg-gray-100 text-gray-600">{t(`changeovers.kind.${v.kind}`)}</span>
                  {current && <span className="text-[10px] rounded px-1.5 py-0.5 bg-primary-600 text-white">{t('changeovers.ai.current')}</span>}
                  <span className="text-[10px] text-gray-400 ml-auto">{new Date(v.at).toLocaleString()}</span>
                </div>
                {v.prompt && <p className="text-xs text-gray-600 mt-1 line-clamp-3">{v.prompt}</p>}
                <div className="flex items-center gap-2 mt-1.5">
                  <span className="text-[10px] text-gray-400">{v.by}{v.costUSD ? ` · $${v.costUSD.toFixed(3)}` : ''}</span>
                  {!current && (
                    <span className="ml-auto flex gap-1.5">
                      <button type="button" onClick={() => onTry(v)} className={`text-[11px] rounded px-2 py-0.5 border ${trying ? 'border-primary-400 text-primary-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}><FiEye size={10} className="inline mr-1" />{t('changeovers.ai.try')}</button>
                      <button type="button" disabled={busy} onClick={() => onRestore(v)} className="text-[11px] rounded px-2 py-0.5 border border-gray-200 text-gray-600 hover:bg-gray-50">{t('changeovers.ai.restore')}</button>
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const DetailsTab = ({ rec, onPatch, onUsePrompt }) => {
  const t = useT();
  const [name, setName] = useState(rec.name);
  useEffect(() => setName(rec.name), [rec.name]);
  const copy = () => navigator.clipboard?.writeText(rec.prompt || '').then(() => toast.success(t('changeovers.details.copied')));
  const info = [
    [t('changeovers.details.createdBy'), rec.userName || '—'],
    [t('changeovers.details.createdAt'), new Date(rec.createdAt).toLocaleString()],
    [t('changeovers.details.duration'), rec.spec ? `${rec.spec.duration.toFixed(2)} s` : '—'],
    [t('changeovers.details.clip'), rec.seconds ? `${rec.seconds.toFixed(2)} s · 1920×1080 · 30 fps` : '—'],
    [t('changeovers.details.versions'), (rec.versions || []).length],
    [t('changeovers.details.cost'), `$${(rec.costUSD || 0).toFixed(3)}`],
    [t('changeovers.details.drawTime'), rec.drawSeconds ? `${rec.drawSeconds} s` : '—'],
  ];
  return (
    <div className="space-y-4">
      <Field label={t('changeovers.details.name')}>
        <input value={name} onChange={(e) => setName(e.target.value)} onBlur={() => name.trim() && name !== rec.name && onPatch({ name })} onKeyDown={(e) => e.key === 'Enter' && e.target.blur()} maxLength={60} className="input w-full text-sm" />
      </Field>
      <Field label={t('changeovers.details.tags')}>
        <TagInput tags={rec.tags || []} onChange={(tags) => onPatch({ tags })} placeholder={t('changeovers.details.tagsPlaceholder')} />
      </Field>
      <Field label={t('changeovers.details.prompt')}>
        <div className="rounded-lg bg-gray-50 border border-gray-200 p-3 text-sm text-gray-700 whitespace-pre-wrap max-h-48 overflow-y-auto">{rec.prompt || '—'}</div>
        <div className="flex flex-wrap gap-1.5 mt-2">
          <button type="button" onClick={copy} className="btn btn-secondary text-xs py-1.5 flex items-center gap-1.5"><FiCopy size={12} /> {t('changeovers.details.copy')}</button>
          <button type="button" onClick={() => onUsePrompt(rec.prompt)} className="btn btn-secondary text-xs py-1.5 flex items-center gap-1.5"><FiPlus size={12} /> {t('changeovers.details.useForNew')}</button>
          <FavoriteButton prompt={rec.prompt} />
        </div>
      </Field>
      <Field label={t('changeovers.details.references')}>
        {!rec.references?.length ? <p className="text-xs text-gray-400">{t('changeovers.details.noReferences')}</p> : (
          <div className="space-y-2">
            {rec.references.map((r) => (
              <div key={r.name + r.frames[0]}>
                <p className="text-[11px] text-gray-500 mb-1 flex items-center gap-1">{r.kind === 'video' ? <FiFilm size={11} /> : <FiImage size={11} />} {r.name}{r.seconds ? ` · ${r.seconds} s` : ''}</p>
                <div className="grid grid-cols-4 gap-1">
                  {r.frames.map((f) => <a key={f} href={changeoverService.refUrl(rec.id, f)} target="_blank" rel="noreferrer"><img src={changeoverService.refUrl(rec.id, f)} alt="" className="w-full aspect-video object-cover rounded" /></a>)}
                </div>
              </div>
            ))}
          </div>
        )}
      </Field>
      <div className="rounded-lg border border-gray-200 divide-y divide-gray-100">
        {info.map(([k, v]) => <div key={k} className="flex justify-between px-3 py-1.5 text-xs"><span className="text-gray-500">{k}</span><span className="text-gray-800">{v}</span></div>)}
      </div>
    </div>
  );
};

// ─── the studio ────────────────────────────────────────────────────────
export const ChangeoverStudio = ({ id, onClose, onUsePrompt, onChanged }) => {
  const t = useT();
  const [rec, setRec] = useState(null);
  const [options, setOptions] = useState(null);
  const [draft, setDraft] = useState(null);
  const [tab, setTab] = useState('design');
  const [view, setView] = useState('live');
  const [saving, setSaving] = useState(false);
  const shownVersion = useRef(null);

  const take = useCallback((r, { resetDraft = false } = {}) => {
    setRec(r);
    // A new current version (saved, AI, restored) becomes the draft.
    if (r.spec && (resetDraft || shownVersion.current !== r.current)) {
      shownVersion.current = r.current;
      setDraft(clone(r.spec));
    }
  }, []);

  useEffect(() => {
    Promise.all([changeoverService.get(id), changeoverService.options()])
      .then(([r, o]) => { setOptions(o); take(r, { resetDraft: true }); })
      .catch((e) => { showError(e); onClose(); });
  }, [id, take, onClose]);

  const busy = isBusy(rec);
  useEffect(() => {
    if (!busy) return undefined;
    const timer = setInterval(() => changeoverService.get(id).then((r) => take(r)).catch(() => {}), 2000);
    return () => clearInterval(timer);
  }, [busy, id, take]);

  const dirty = rec?.spec && draft && !same(draft, rec.spec);
  const up = (fn) => setDraft((d) => { const n = clone(d); fn(n); return n; });

  const close = useCallback(() => {
    if (dirty && !window.confirm(t('changeovers.studio.unsaved'))) return;
    onClose();
  }, [dirty, onClose, t]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close]);

  const save = async () => {
    setSaving(true);
    try {
      take(await changeoverService.save(id, draft), { resetDraft: true });
      setView('live');
      onChanged?.();
      toast.success(t('changeovers.studio.saved'));
      return true;
    } catch (e) {
      showError(e);
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Download is always the design on screen: unsaved edits are saved first,
  // and the file comes once its drawing has finished.
  const [wantDownload, setWantDownload] = useState(false);
  const download = async () => {
    if (dirty && !(await save())) return;
    setWantDownload(true);
  };
  useEffect(() => {
    if (!wantDownload || !rec) return;
    if (rec.status === 'failed') { setWantDownload(false); return; }
    if (rec.status !== 'ready' || dirty) return;
    setWantDownload(false);
    const a = document.createElement('a');
    a.href = changeoverService.fileUrl(id, 'video', rec.rendered, { download: true });
    document.body.appendChild(a);
    a.click();
    a.remove();
  }, [wantDownload, rec, dirty, id]);
  const aiApply = async (prompt) => {
    try { take(await changeoverService.ai(id, prompt, draft)); onChanged?.(); } catch (e) { showError(e); }
  };
  const restore = async (v) => {
    try { take(await changeoverService.restore(id, v.n), { resetDraft: true }); onChanged?.(); } catch (e) { showError(e); }
  };
  const patch = async (data) => {
    try { const r = await changeoverService.patch(id, data); setRec((cur) => ({ ...cur, name: r.name, tags: r.tags })); onChanged?.(); } catch (e) { showError(e); }
  };
  const duplicate = async () => {
    try { await changeoverService.duplicate(id); toast.success(t('changeovers.duplicated')); onChanged?.(); } catch (e) { showError(e); }
  };
  const remove = async () => {
    if (!window.confirm(t('changeovers.removeConfirm', { name: rec.name }))) return;
    try { await changeoverService.remove(id); onChanged?.(); onClose(); } catch (e) { showError(e); }
  };
  const retry = async () => {
    try { take(await changeoverService.retry(id)); } catch (e) { showError(e); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-gray-50 flex flex-col">
      <div className="flex items-center gap-3 px-4 py-2.5 bg-white border-b border-gray-200">
        <button type="button" onClick={close} className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"><FiX size={18} /></button>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-gray-900 truncate">{rec?.name || '…'}</p>
          {rec && <div className="max-w-xs"><StatusLine item={rec} /></div>}
        </div>
        {rec?.status === 'failed' && <button type="button" onClick={retry} className="btn btn-secondary text-sm flex items-center gap-1.5"><FiRefreshCw size={14} /> {t('changeovers.retry')}</button>}
        {dirty && (
          <>
            <span className="text-xs text-amber-600 hidden sm:inline">{t('changeovers.studio.unsavedShort')}</span>
            <button type="button" onClick={() => setDraft(clone(rec.spec))} className="btn btn-secondary text-sm flex items-center gap-1.5"><FiRotateCcw size={14} /> {t('changeovers.studio.discard')}</button>
            <button type="button" onClick={save} disabled={saving || rec.status === 'designing'} className="btn btn-primary text-sm flex items-center gap-1.5"><FiSave size={14} /> {saving ? t('changeovers.sending') : t('changeovers.studio.save')}</button>
          </>
        )}
        {rec?.spec && (
          <button type="button" onClick={download} disabled={wantDownload || saving} title={dirty ? t('changeovers.studio.downloadSaves') : undefined} className="btn btn-secondary text-sm flex items-center gap-1.5">
            {wantDownload ? <FiRefreshCw size={14} className="animate-spin" /> : <FiDownload size={14} />}
            <span className="hidden md:inline">
              {wantDownload ? t('changeovers.studio.preparingDownload', { pct: Math.round((rec.progress || 0) * 100) }) : t('changeovers.download')}
            </span>
          </button>
        )}
        <button type="button" onClick={duplicate} title={t('changeovers.duplicate')} className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"><FiCopy size={16} /></button>
        {!busy && <button type="button" onClick={remove} title={t('changeovers.remove')} className="p-2 rounded-lg hover:bg-red-50 text-gray-500 hover:text-red-600"><FiTrash2 size={16} /></button>}
      </div>

      {!rec || !options || !draft ? (
        <div className="flex-1 flex items-center justify-center text-gray-400 gap-2">
          {rec?.status === 'designing' ? <><FiRefreshCw className="animate-spin" /> {t('changeovers.designing')}</> : rec?.status === 'failed' ? <span className="text-red-600 text-sm">{rec.error}</span> : <FiRefreshCw className="animate-spin" />}
        </div>
      ) : (
        <div className="flex-1 min-h-0 flex flex-col lg:flex-row">
          <div className="flex-1 min-w-0 overflow-y-auto p-4 lg:p-6 space-y-3">
            <div className="flex items-center gap-1 p-0.5 rounded-lg bg-gray-200/70 w-fit text-sm">
              {[['live', t('changeovers.studio.live')], ['final', t('changeovers.studio.final')]].map(([v, label]) => (
                <button key={v} type="button" onClick={() => setView(v)} className={`px-3 py-1.5 rounded-md ${view === v ? 'bg-white shadow-sm text-gray-900 font-medium' : 'text-gray-600'}`}>{label}</button>
              ))}
            </div>
            <div className="max-w-5xl">
              {view === 'live' ? <LivePreview spec={draft} /> : (
                <div className="relative w-full aspect-video bg-black rounded-xl overflow-hidden">
                  {(rec.rendered > 0 || rec.status === 'ready') && <video key={rec.rendered || 0} src={changeoverService.fileUrl(id, 'video', rec.rendered)} controls autoPlay loop className="w-full h-full" />}
                  {busy && <div className="absolute left-2 top-2 rounded-lg bg-black/70 text-white text-xs px-2.5 py-1.5">{rec.status === 'drawing' ? t('changeovers.drawing', { pct: Math.round((rec.progress || 0) * 100) }) : t('changeovers.designing')}</div>}
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500 max-w-5xl">{view === 'live' ? t('changeovers.studio.liveNote') : t('changeovers.previewNote')}</p>
          </div>
          <div className="w-full lg:w-[440px] flex-shrink-0 border-t lg:border-t-0 lg:border-l border-gray-200 bg-white flex flex-col min-h-0">
            <div className="flex border-b border-gray-200 px-2">
              {[['design', t('changeovers.tab.design')], ['ai', t('changeovers.tab.ai')], ['details', t('changeovers.tab.details')]].map(([v, label]) => (
                <button key={v} type="button" onClick={() => setTab(v)} className={`px-3.5 py-2.5 text-sm border-b-2 -mb-px ${tab === v ? 'border-primary-500 text-primary-700 font-medium' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>{label}</button>
              ))}
            </div>
            <div className="flex-1 overflow-y-auto p-3">
              {tab === 'design' && <DesignTab draft={draft} up={up} options={options} />}
              {tab === 'ai' && <AiTab rec={rec} draft={draft} busy={busy} onApply={aiApply} onTry={(v) => { setDraft(clone(v.spec)); setView('live'); }} onRestore={restore} />}
              {tab === 'details' && <DetailsTab rec={rec} onPatch={patch} onUsePrompt={onUsePrompt} />}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChangeoverStudio;
